import type { StorageLocation } from "@/types/api";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { inventoryApi } from "./api";
import { storageCategoriesKeys } from "./queryKeys";

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (location: StorageLocation) => void;
  initial?: StorageLocation | null;
}

export function StorageLocationDialog({ open, onClose, onSave, initial }: Props) {
  const [categoryId, setCategoryId] = useState<number | "">("");
  const [bin, setBin] = useState("");
  const [slot, setSlot] = useState("");

  const categories = useQuery({
    queryKey: storageCategoriesKeys.all,
    queryFn: inventoryApi.listStorageCategories,
  });

  useEffect(() => {
    if (!open) return;
    const cat = initial?.categoryInfo?.id ?? initial?.category ?? "";
    setCategoryId(cat);
    setBin(initial?.bin ?? "");
    setSlot(initial?.slot ?? "");
  }, [open, initial]);

  const selectedCategory = (categories.data ?? []).find((c) => c.id === categoryId);
  const isOrganized = selectedCategory?.isInOrganizedStorage ?? false;

  const ensure = useMutation({
    mutationFn: () =>
      inventoryApi.ensureStorageLocation({
        categoryId: categoryId as number,
        bin: isOrganized ? bin : "",
        slot: isOrganized ? slot : "",
      }),
    onSuccess: (location) => onSave(location),
  });

  function handleOk() {
    if (!categoryId) return;
    ensure.mutate();
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Configure Storage Location</DialogTitle>
      <DialogContent>
        <TextField
          select
          required
          fullWidth
          label="Storage Location"
          variant="standard"
          margin="dense"
          value={categoryId}
          onChange={(e) => setCategoryId(Number(e.target.value))}
        >
          {(categories.data ?? []).map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        {isOrganized && (
          <>
            <TextField
              fullWidth
              label="Bin"
              variant="standard"
              margin="dense"
              value={bin}
              onChange={(e) => setBin(e.target.value)}
            />
            <TextField
              fullWidth
              label="Slot"
              variant="standard"
              margin="dense"
              value={slot}
              onChange={(e) => setSlot(e.target.value)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="inherit">
          CANCEL
        </Button>
        <Button
          onClick={handleOk}
          variant="contained"
          color="error"
          disabled={!categoryId || ensure.isPending}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}
